import request from "../utils/request";
import { normalizePaginationInteger, type PaginationIntegerValue } from "./pagination";

// ─── 类型定义 ─────────────────────────────────────────────────────────────────

export interface FeedbackStatistics {
  totalAnswers: number;
  usefulCount: number;
  uselessCount: number;
  feedbackRate: string;
  usefulRate: string;
  uselessRate: string;
}

export interface SourceDocument {
  FileName: string;
}

export interface FeedbackItem {
  ID: string;
  FeedbackTime: string;
  Type: "useful" | "useless";
  Question: string;
  ConversationID: string;
  UpdateByName: string;
  /** 列表仅返回来源文档数量；完整文档数组见 FeedbackDetail.SourceDocuments */
  SourceDocumentCount: number;
  FeedbackFlags: string;
  FeedbackDescription: string;
  InternalNote: string;
}

export interface FeedbackDetail extends FeedbackItem {
  SourceDocuments: SourceDocument[];
  SystemAnswer: string;
}

export interface FeedbackSearchParams {
  keyword?: string;
  type?: "useful" | "useless";
  startTime?: string;
  endTime?: string;
  Page?: number;
  Limit?: number;
}

export interface FeedbackSearchResponse {
  Count: number;
  Page: number;
  Limit: number;
  Data: FeedbackItem[];
}

interface FeedbackSearchWire {
  Count?: PaginationIntegerValue;
  Page?: PaginationIntegerValue;
  Limit?: PaginationIntegerValue;
  Data?: FeedbackItem[];
}

// ─── 工具函数 ─────────────────────────────────────────────────────────────────

/** 解析 FeedbackFlags 字符串为数组，"None" 返回空数组，PascalCase 转为句式大写 */
export function parseFeedbackFlags(flags: string): string[] {
  if (!flags || flags === "None") return [];
  return flags.split(", ").map((flag) => {
    const words = flag.replace(/([A-Z])/g, " $1").trim();
    return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
  });
}

// ─── API 方法 ─────────────────────────────────────────────────────────────────

/** 获取反馈统计信息 */
export function getStatistics() {
  return request.get<FeedbackStatistics>("/project/answerfeedback/statistics");
}

/** 分页搜索反馈列表 */
export async function searchFeedback(params: FeedbackSearchParams) {
  const response = await request.post<FeedbackSearchWire>("/project/answerfeedback/search", params);
  const body = response.data ?? {};
  const data = {
    Count: normalizePaginationInteger(body.Count, 0, 0),
    Page: normalizePaginationInteger(body.Page, params.Page ?? 1, 1),
    Limit: normalizePaginationInteger(body.Limit, params.Limit ?? 20, 1),
    Data: body.Data ?? [],
  } satisfies FeedbackSearchResponse;
  return { ...response, data };
}

/** 获取反馈详情 */
export function getFeedbackDetail(id: string) {
  return request.get<FeedbackDetail>(`/project/answerfeedback/${id}`);
}

/** 更新内部备注 */
export function updateInternalNote(id: string, note: string) {
  return request.put<FeedbackDetail>(`/project/answerfeedback/${id}/note`, { InternalNote: note });
}

/** 更新反馈意见描述 */
export function updateFeedbackDescription(id: string, description: string) {
  return request.put<FeedbackDetail>(`/project/answerfeedback/${id}/feedback-description`, {
    FeedbackDescription: description,
  });
}

/** 导出反馈 Excel */
export function exportFeedback(params: FeedbackSearchParams) {
  return request.post("/project/answerfeedback/export", params, {
    responseType: "blob",
  });
}
